import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { YearlyEvent } from '../constant/YearlyEvent';
import { auth, db, provider } from '../firebase';
import FirebaseApi from '../middleware/firebaseApi';
import apis from '../apis';

const DataContext = createContext();

export const SESSION_DURATION_MS = 60 * 60 * 1000; // 60 minutes
export const SESSION_WARNING_MS = 3 * 60 * 1000;  // 3 minutes before expiration warning
const SESSION_ACTIVITY_THROTTLE_MS = 30 * 1000;    // Throttle user activity writes to 30s
const STORAGE_KEY_LAST_ACTIVE = 'apcs_session_last_active';
const STORAGE_KEY_USER_ID = 'apcs_session_user_id';
const STORAGE_KEY_LEGACY_START = 'sessionStart';

const clearSessionStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY_LAST_ACTIVE);
        localStorage.removeItem(STORAGE_KEY_USER_ID);
        localStorage.removeItem(STORAGE_KEY_LEGACY_START);
    } catch (e) {
        console.error("Failed to clear session storage:", e);
    }
};

const initSessionStorage = (uid) => {
    try {
        const now = Date.now().toString();
        localStorage.setItem(STORAGE_KEY_LAST_ACTIVE, now);
        if (uid) {
            localStorage.setItem(STORAGE_KEY_USER_ID, uid);
        }
        localStorage.setItem(STORAGE_KEY_LEGACY_START, now);
    } catch (e) {
        console.error("Failed to init session storage:", e);
    }
};

const getStoredLastActive = () => {
    try {
        const lastActive = localStorage.getItem(STORAGE_KEY_LAST_ACTIVE) || localStorage.getItem(STORAGE_KEY_LEGACY_START);
        return lastActive ? parseInt(lastActive, 10) : null;
    } catch (e) {
        return null;
    }
};

const getStoredSessionUid = () => {
    try {
        return localStorage.getItem(STORAGE_KEY_USER_ID);
    } catch (e) {
        return null;
    }
};

export const useAuth = () => useContext(DataContext);

export const DataContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallMobileAndSmaller = useMediaQuery(theme.breakpoints.down('sm')); // 600
    const isMobileAndSmaller = useMediaQuery(theme.breakpoints.down('md'));// 900
    const isMobileAndBigger = useMediaQuery(theme.breakpoints.up('md'));
    const isLaptopAndSmaller = useMediaQuery(theme.breakpoints.down('lg'));

    const [loggedInUser, setLoggedInUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageHomeLoaded, setImageHomeLoaded] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(YearlyEvent.APCSTHESOUNDOFASIA2025);
    const [currentEventId, setCurrentEventId] = useState('APCS2026');
    const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(true);
    const [copyrightText, setCopyrightText] = useState('© 2026 APCS Music. All Rights Reserved.');

    const [sessionRemainingMs, setSessionRemainingMs] = useState(SESSION_DURATION_MS);
    const lastActivityRecordedRef = useRef(Date.now());
    const isLoggingInRef = useRef(false);

    useEffect(() => {
        const fetchGlobalSettings = async () => {
            try {
                const response = await apis.systemSettings.getGlobalSettings();
                if (response?.data?.currentEventId) {
                    setCurrentEventId(response.data.currentEventId);
                }
                if (response?.data?.isRegistrationEnabled !== undefined) {
                    setIsRegistrationEnabled(response.data.isRegistrationEnabled);
                } else {
                    setIsRegistrationEnabled(true);
                }
                if (response?.data?.copyrightText) {
                    setCopyrightText(response.data.copyrightText);
                }
            } catch (error) {
                console.error("Error fetching global settings:", error);
            }
        };
        fetchGlobalSettings();
    }, []);

    // Explicitly extend or refresh the active session
    const extendSession = useCallback(() => {
        const now = Date.now();
        lastActivityRecordedRef.current = now;
        try {
            localStorage.setItem(STORAGE_KEY_LAST_ACTIVE, now.toString());
            localStorage.setItem(STORAGE_KEY_LEGACY_START, now.toString());
        } catch (e) {
            console.error("Failed to extend session:", e);
        }
        setSessionRemainingMs(SESSION_DURATION_MS);
    }, []);

    // Sliding user activity listener (throttled to update at most once every 30s)
    useEffect(() => {
        if (!loggedInUser) return;

        const handleUserActivity = () => {
            const now = Date.now();
            if (now - lastActivityRecordedRef.current >= SESSION_ACTIVITY_THROTTLE_MS) {
                lastActivityRecordedRef.current = now;
                try {
                    localStorage.setItem(STORAGE_KEY_LAST_ACTIVE, now.toString());
                    localStorage.setItem(STORAGE_KEY_LEGACY_START, now.toString());
                } catch (e) {
                    // Ignore localStorage write error
                }
            }
        };

        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));

        return () => {
            events.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
        };
    }, [loggedInUser]);

    // Active session interval checking remaining time and triggering auto-logout
    useEffect(() => {
        if (!loggedInUser) {
            setSessionRemainingMs(SESSION_DURATION_MS);
            return;
        }

        const checkSessionInterval = setInterval(async () => {
            const lastActive = getStoredLastActive();
            if (!lastActive) return;

            const now = Date.now();
            const elapsed = now - lastActive;
            const remaining = SESSION_DURATION_MS - elapsed;

            if (remaining <= 0) {
                clearInterval(checkSessionInterval);
                setSessionRemainingMs(0);
                console.log("Session expired due to inactivity. Logging out...");
                try {
                    await auth.signOut();
                    clearSessionStorage();
                    setLoggedInUser(null);
                    navigate('/login');
                    toast.info("Your session has expired due to inactivity. Please log in again.");
                } catch (err) {
                    console.error("Error logging out expired session:", err);
                }
            } else {
                setSessionRemainingMs(remaining);
            }
        }, 2000);

        return () => clearInterval(checkSessionInterval);
    }, [loggedInUser, navigate]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            // Helper function to handle logout
            const forceLogout = async (toastMessage = "Session expired. Please log in again.") => {
                try {
                    await auth.signOut();
                } catch (e) {
                    console.error("Error during forceLogout signOut:", e);
                }
                clearSessionStorage();
                setLoggedInUser(null);
                if (window.location.pathname !== '/login') {
                    navigate("/login");
                    if (toastMessage) {
                        toast.info(toastMessage);
                    }
                }
            };

            if (user) {
                // If a login is actively in progress via signInWithEmail or signInWithGoogle,
                // do not perform expiration checks here because the credentials were just provided.
                if (isLoggingInRef.current) {
                    initSessionStorage(user.uid);
                } else {
                    const lastActive = getStoredLastActive();
                    const sessionUid = getStoredSessionUid();

                    // If user ID doesn't match or no session timestamp recorded, initialize it for this valid Firebase user
                    if (!lastActive || (sessionUid && sessionUid !== user.uid)) {
                        initSessionStorage(user.uid);
                    } else {
                        const now = Date.now();
                        const timeElapsed = now - lastActive;

                        // Check if the previous session has already expired
                        if (timeElapsed > SESSION_DURATION_MS) {
                            console.log("Session expired on load. Logging out...");
                            await forceLogout();
                            setLoading(false);
                            return; // Stop execution here
                        }
                    }
                }

                try {
                    const token = await user.getIdToken();
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);

                    let firestoreData = {};
                    if (userDocSnap.exists()) {
                        firestoreData = userDocSnap.data();
                    }

                    setLoggedInUser({
                        ...user,
                        token,
                        ...firestoreData
                    });

                    const userRole = firestoreData.role;

                    // Only navigate if we are currently on the login page
                    if (window.location.pathname === '/login') {
                        if (userRole === 'admin' || userRole === 'subadmin') {
                            navigate("/adminDashboard");
                        } else if (userRole === 'jury') {
                            navigate("/juryDashboard");
                        } else {
                            navigate("/login");
                        }
                    }

                } catch (error) {
                    console.error("Error fetching user details:", error);
                    setLoggedInUser(null);
                }
            } else {
                // No user found
                clearSessionStorage();
                setLoggedInUser(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    const signInWithGoogle = async () => {
        isLoggingInRef.current = true;
        initSessionStorage();
        try {
            // 1. Popup Signin
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            initSessionStorage(user.uid);

            // 2. Check if user exists in Firestore
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);

            let userRole = null;

            if (userDocSnap.exists()) {
                userRole = userDocSnap.data().role;
            } else {
                const whitelistDatas = await FirebaseApi.getWhitelist();
                const isWhitelisted = whitelistDatas.some(data => data.id === user.email);

                if (isWhitelisted) {
                    userRole = "admin";
                    await setDoc(userDocRef, {
                        email: user.email,
                        role: userRole,
                        createdAt: new Date()
                    });
                }
            }

            // 3. Final Access Check
            if (userRole) {
                const token = await user.getIdToken();
                setLoggedInUser({ ...user, token, role: userRole });

                if (userRole === 'admin' || userRole === 'subadmin') {
                    navigate("/adminDashboard");
                } else if (userRole === 'jury') {
                    navigate("/juryDashboard");
                }
            } else {
                toast.error("Account is not authorized.");
                await auth.signOut();
                clearSessionStorage();
                setLoggedInUser(null);
            }

        } catch (error) {
            clearSessionStorage();
            console.error("Login Error:", error);
            toast.error("Login Failed");
        } finally {
            isLoggingInRef.current = false;
        }
    };

    const signInWithEmail = async (email, password) => {
        isLoggingInRef.current = true;
        initSessionStorage();
        try {
            // 1. Authenticate with Firebase Auth
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            initSessionStorage(user.uid);

            // 2. Check if user exists in Firestore 'users' collection
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);

            let userData = null;
            let userRole = null;

            if (userDocSnap.exists()) {
                const userDataSnap = userDocSnap.data();
                userData = userDataSnap;
                userRole = userData?.role;
            }

            // 3. Final Access Check & State Update
            if (userRole) {
                // --- Jury deadline enforcement ---
                if (userRole === 'jury' && userData?.competitionCategory) {
                    try {
                        const settingsRes = await apis.systemSettings.getGlobalSettings();
                        const juryDeadlines = settingsRes?.data?.juryDeadlines;
                        if (juryDeadlines) {
                            const categoryDeadline = juryDeadlines[userData.competitionCategory];
                            if (categoryDeadline && new Date() >= new Date(categoryDeadline)) {
                                await auth.signOut();
                                clearSessionStorage();
                                setLoggedInUser(null);
                                throw new Error(
                                    `The scoring period for ${userData.competitionCategory} has ended. You can no longer submit scores. Please contact admin for assistance.`
                                );
                            }
                        }
                    } catch (deadlineErr) {
                        if (deadlineErr.message?.includes('scoring period')) throw deadlineErr;
                        console.error("Error checking jury deadline:", deadlineErr);
                    }
                }

                const token = await user.getIdToken();

                // This updates the global app state
                setLoggedInUser({
                    ...user,
                    ...userData,
                    token,
                    createdAt: userData.createdAt?.toDate()
                });

                // Navigation
                if (userRole === 'admin' || userRole === 'subadmin') {
                    navigate("/adminDashboard");
                } else {
                    // Handle other roles (e.g., jury)
                    navigate("/juryDashboard");
                }
            } else {
                toast.error("Account is not authorized.");
                await auth.signOut();
                clearSessionStorage();
                setLoggedInUser(null);
            }

        } catch (error) {
            clearSessionStorage();
            console.error("Email Login Error:", error);
            let msg = "Login failed.";
            if (error.code === 'auth/invalid-credential') {
                msg = "Invalid email or password.";
            } else if (error.message && error.message.includes('scoring period')) {
                msg = error.message;
            }
            throw new Error(msg); // Throw error so Login component can handle UI (loading state)
        } finally {
            isLoggingInRef.current = false;
        }
    };

    const signOut = async (withoutToast = false) => {
        try {
            await auth.signOut();
            clearSessionStorage();
            setLoggedInUser(null);
            if (!withoutToast) {
                toast.success("Successfully Sign Out");
            }
        } catch (e) {
            console.error("Sign out error", e);
            toast.error("Sign Out Failed");
        }
    };

    const DataContextValue = {
        loggedInUser,
        signInWithGoogle,
        signInWithEmail,
        signOut,
        loading,
        setImageHomeLoaded,
        imageHomeLoaded,
        setSelectedEvent,
        selectedEvent,
        currentEventId,
        setCurrentEventId,
        isRegistrationEnabled,
        copyrightText,
        isMobileAndSmaller,
        isMobileAndBigger,
        isSmallMobileAndSmaller,
        isLaptopAndSmaller,
        sessionRemainingMs,
        extendSession,
        SESSION_DURATION_MS,
        SESSION_WARNING_MS,
    };

    return (
        <DataContext.Provider value={DataContextValue}>
            {!loading && children}
        </DataContext.Provider>
    );
};
